import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Divider,
  Grid,
  Chip,
  Tab,
  Tabs,
  TabPanel,
} from '@mui/material';
import { useAuthHybrid, useAuthRedux, useAuthLegacy } from '../hooks/useAuthHybrid';
import { useAuth } from '../contexts/AuthContext';

// Composant de démonstration pour les 3 approches d'authentification
const AuthMigrationDemo = () => {
  const [tabValue, setTabValue] = useState(0);
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Démonstration - Migration Context → Redux
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Cette page montre comment migrer progressivement de AuthContext vers Redux
        sans casser l'application existante.
      </Alert>
      
      <Tabs 
        value={tabValue} 
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Hook Hybride (Recommandé)" />
        <Tab label="Redux Pur (Nouveau)" />
        <Tab label="Context Legacy" />
      </Tabs>
      
      {tabValue === 0 && <HybridAuthDemo />}
      {tabValue === 1 && <ReduxAuthDemo />}
      {tabValue === 2 && <LegacyAuthDemo />}
    </Box>
  );
};

// Composant utilisant le hook hybride
const HybridAuthDemo = () => {
  const auth = useAuthHybrid();
  
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Hook Hybride - useAuthHybrid()
        </Typography>
        
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Meilleure approche pour la migration progressive
        </Alert>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              État d'authentification :
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={auth.isAuthenticated ? 'Connecté' : 'Non connecté'}
                color={auth.isAuthenticated ? 'success' : 'default'}
                sx={{ mr: 1 }}
              />
              {auth.__source.redux && (
                <Chip label="Source: Redux" color="primary" size="small" />
              )}
              {auth.__source.context && (
                <Chip label="Source: Context" color="secondary" size="small" />
              )}
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              Utilisateur: {auth.user?.email || 'Aucun'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Token: {auth.token ? 'Présent' : 'Absent'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Actions disponibles :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => auth.updateUser({ email: 'test@hybrid.com' })}
              >
                Simuler mise à jour utilisateur
              </Button>
              <Button 
                variant="outlined" 
                color="error"
                size="small"
                onClick={auth.logout}
              >
                Déconnexion
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="textSecondary">
          <strong>Avantages :</strong> Compatible avec l'existant, transition douce, 
          debugging facilité, source de vérité unifiée.
        </Typography>
      </CardContent>
    </Card>
  );
};

// Composant utilisant Redux pur
const ReduxAuthDemo = () => {
  const auth = useAuthRedux();
  
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Redux Pur - useAuthRedux()
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          🎯 Destiné aux nouveaux composants ou composants migrés
        </Alert>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              État Redux :
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={auth.isAuthenticated ? 'Connecté' : 'Non connecté'}
                color={auth.isAuthenticated ? 'success' : 'default'}
                sx={{ mr: 1 }}
              />
              <Chip label="Redux" color="primary" size="small" />
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              Utilisateur: {auth.user?.email || 'Aucun'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Loading: {auth.isLoading ? 'Oui' : 'Non'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Erreur: {auth.error || 'Aucune'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Actions Redux :
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => auth.updateUser({ 
                  email: 'redux@example.com',
                  firstName: 'Redux',
                  lastName: 'User'
                })}
              >
                Mettre à jour via Redux
              </Button>
              <Button 
                variant="outlined" 
                color="error"
                size="small"
                onClick={auth.logout}
              >
                Déconnexion Redux
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="textSecondary">
          <strong>Avantages :</strong> Performance optimale, DevTools, 
          persistance, API unifiée, typage strict.
        </Typography>
      </CardContent>
    </Card>
  );
};

// Composant utilisant le Context legacy
const LegacyAuthDemo = () => {
  let auth;
  let hasError = false;
  
  try {
    auth = useAuthLegacy();
  } catch (error) {
    hasError = true;
    auth = { isAuthenticated: false, user: null };
  }
  
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Context Legacy - useAuthLegacy()
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ Destiné aux composants qui ne peuvent pas encore migrer
        </Alert>
        
        {hasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Erreur: AuthContext non disponible dans ce contexte
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              État Context :
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={auth.isAuthenticated ? 'Connecté' : 'Non connecté'}
                color={auth.isAuthenticated ? 'success' : 'default'}
                sx={{ mr: 1 }}
              />
              <Chip label="Context" color="secondary" size="small" />
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              Utilisateur: {auth.user?.email || 'Aucun'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Actions Context :
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Actions limitées disponibles via le Context existant
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="textSecondary">
          <strong>Limitations :</strong> Pas de persistance, pas de DevTools, 
          pas de typage strict, API limitée.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AuthMigrationDemo;
